import React from "react";
import { connect } from "react-redux";
import {
  Box,
  Maximize2,
  Minimize2,
  X,
  RotateCcw,
  Smartphone,
  ChevronDown,
  ExternalLink,
} from "lucide-react";
import "../styles/ARView.css";

// Preset 3D models — replace URLs with your own .glb files when ready
const PRESET_MODELS = [
    {
    id: "locker-module",
    name: "Módulo Taquillas",
    description: "Módulo estándar Vacway",
    url: "https://vacway-images.s3.eu-central-1.amazonaws.com/scene.glb",
    iosUrl: "https://vacway-images.s3.eu-central-1.amazonaws.com/scene.usdz",
    poster: null,
  },
  {
    id: "astronaut",
    name: "Astronauta (Demo)",
    description: "Figura a tamaño real",
    url: "https://modelviewer.dev/shared-assets/models/Astronaut.glb",
    poster: null,
  },
  {
    id: "chair",
    name: "Robot (Demo)",
    description: "Robot expresivo animado",
    url: "https://modelviewer.dev/shared-assets/models/RobotExpressive.glb",
    poster: null,
  },
  {
    id: "refrigerator",
    name: "Armario (Demo)",
    description: "Armario con puertas de cristal — similar a taquilla",
    url: "https://raw.githubusercontent.com/KhronosGroup/glTF-Sample-Assets/main/Models/CommercialRefrigerator/glTF-Binary/CommercialRefrigerator.glb",
    poster: null,
  },
];

class ARView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedModelId: PRESET_MODELS[0].id,
      isFullscreen: false,
      supportsAR: false,
      selectorOpen: false,
      isAndroid: false,
      isIOS: false,
    };
    this.modelViewerRef = React.createRef();
    this.fullscreenContainerRef = React.createRef();
    this.toggleFullscreen = this.toggleFullscreen.bind(this);
    this.resetCamera = this.resetCamera.bind(this);
    this.handleFullscreenChange = this.handleFullscreenChange.bind(this);
    this.selectModel = this.selectModel.bind(this);
  }

  componentDidMount() {
    document.addEventListener("fullscreenchange", this.handleFullscreenChange);
    document.addEventListener(
      "webkitfullscreenchange",
      this.handleFullscreenChange
    );

    // Detect AR support
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    this.setState({ isIOS, isAndroid });
    if (isIOS) {
      // iOS uses AR Quick Look — always available on modern iOS devices
      this.setState({ supportsAR: true });
    } else if (isAndroid && navigator.xr) {
      navigator.xr.isSessionSupported('immersive-ar').then((supported) => {
        this.setState({ supportsAR: supported });
      }).catch(() => {
        this.setState({ supportsAR: false });
      });
    } else if (isAndroid) {
      // Android without WebXR — scene-viewer (ARCore) may still work;
      // model-viewer will show/hide ar-button automatically
      this.setState({ supportsAR: false });
    } else {
      this.setState({ supportsAR: false });
    }
  }

  componentWillUnmount() {
    document.removeEventListener(
      "fullscreenchange",
      this.handleFullscreenChange
    );
    document.removeEventListener(
      "webkitfullscreenchange",
      this.handleFullscreenChange
    );
  }

  handleFullscreenChange() {
    const isFullscreen = !!(
      document.fullscreenElement || document.webkitFullscreenElement
    );
    this.setState({ isFullscreen });
  }

  selectModel(id) {
    this.setState({ selectedModelId: id, selectorOpen: false });
    // Reset camera when switching models
    setTimeout(() => this.resetCamera(), 300);
  }

  async toggleFullscreen() {
    const container = this.fullscreenContainerRef.current;
    if (!container) return;

    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      try {
        if (container.requestFullscreen) {
          await container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          await container.webkitRequestFullscreen();
        }
      } catch {
        // Fullscreen not supported or blocked
      }
    } else {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      }
    }
  }

  resetCamera() {
    const viewer = this.modelViewerRef.current;
    if (viewer) {
      viewer.cameraOrbit = "auto auto auto";
      viewer.fieldOfView = "auto";
      viewer.jumpCameraToGoal();
    }
  }

  render() {
    const { selectedModelId, isFullscreen, supportsAR, selectorOpen, isAndroid, isIOS } =
      this.state;

    const selectedModel = PRESET_MODELS.find((m) => m.id === selectedModelId);
    const androidArSrc = `/android-ar.html?src=${encodeURIComponent(selectedModel.url)}&name=${encodeURIComponent(selectedModel.name)}`;
    // Show Android pseudo-AR card on Android devices, and also on desktop for
    // previewing / testing. Hide it on iOS, which already works via Quick Look.
    const showAndroidCard = !isIOS;

    return (
      <div className="ar-view">
        <div className="ar-header">
          <div className="ar-header-content">
            <div className="ar-header-text">
              <h1>
                <Box size={28} className="ar-header-icon" />
                Visor AR
              </h1>
              <span className="ar-subtitle">
                Visualiza módulos en 3D y realidad aumentada
              </span>
            </div>
            <div className="ar-header-actions">
              <div className="ar-model-selector">
                <button
                  className="ar-selector-btn"
                  onClick={() => this.setState({ selectorOpen: !selectorOpen })}
                >
                  <Box size={16} />
                  <span>{selectedModel.name}</span>
                  <ChevronDown
                    size={16}
                    className={`ar-chevron ${selectorOpen ? "ar-chevron-open" : ""}`}
                  />
                </button>
                {selectorOpen && (
                  <div className="ar-selector-dropdown">
                    {PRESET_MODELS.map((model) => (
                      <button
                        key={model.id}
                        className={`ar-selector-item ${model.id === selectedModelId ? "ar-selector-item-active" : ""}`}
                        onClick={() => this.selectModel(model.id)}
                      >
                        <div className="ar-selector-item-info">
                          <span className="ar-selector-item-name">
                            {model.name}
                          </span>
                          <span className="ar-selector-item-desc">
                            {model.description}
                          </span>
                        </div>
                        {model.id === selectedModelId && (
                          <span className="ar-selector-check">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="ar-cards">

          {/* Card 1: 3D Preview */}
          <div
            className={`ar-card ${isFullscreen ? "ar-card-fullscreen" : ""}`}
            ref={this.fullscreenContainerRef}
          >
            <div className="ar-card-header">
              <span className="ar-card-title">
                <Box size={15} /> Vista 3D
              </span>
              <div className="ar-card-controls">
                <button className="ar-toolbar-btn" onClick={this.resetCamera} title="Resetear cámara">
                  <RotateCcw size={15} />
                </button>
                <button className="ar-toolbar-btn" onClick={this.toggleFullscreen} title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}>
                  {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                </button>
                {isFullscreen && (
                  <button className="ar-toolbar-btn ar-toolbar-close" onClick={this.toggleFullscreen}>
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
            <model-viewer
              ref={this.modelViewerRef}
              src={selectedModel.url}
              ios-src={selectedModel.iosUrl || undefined}
              alt={selectedModel.name}
              ar
              ar-modes="webxr scene-viewer quick-look"
              camera-controls
              auto-rotate
              shadow-intensity="1"
              shadow-softness="0.5"
              exposure="1"
              environment-image="neutral"
              style={{
                width: "100%",
                height: isFullscreen ? "100vh" : "280px",
                backgroundColor: "#1a1a2e",
                borderRadius: isFullscreen ? "0" : "0 0 12px 12px",
              }}
            >
              <button className="ar-activate-btn" slot="ar-button">
                <Smartphone size={16} />
                <span>Ver en AR</span>
              </button>
              <div className="ar-viewer-progress" slot="progress-bar">
                <div className="ar-progress-track">
                  <div className="ar-progress-fill"></div>
                </div>
              </div>
            </model-viewer>
            {!supportsAR && (
              <div className="ar-desktop-notice">
                <Smartphone size={13} />
                <span>
                  AR Nativo no disponible en este dispositivo
                  {isAndroid ? " — prueba el visor Android de abajo" : ""}
                </span>
              </div>
            )}
          </div>

          {/* Card 2: Android pseudo-AR viewer (no Google Play Services required) */}
          {showAndroidCard && (
            <div className="ar-card">
              <div className="ar-card-header">
                <span className="ar-card-title">
                  <Smartphone size={15} /> Visor AR Android
                </span>
                <span className="ar-card-badge">Sin Google Play Services</span>
              </div>
              <div className="ar-scan-body">
                <p className="ar-scan-desc">
                  Si el botón <strong>Ver en AR</strong> no funciona en tu
                  Android (falta ARCore / Google Play Services), usa este visor
                  alternativo. Combina la cámara del dispositivo con el
                  giroscopio para mostrar <strong>{selectedModel.name}</strong>{" "}
                  en una realidad aumentada ligera — sin instalar nada.
                </p>
                <ul className="ar-android-tips">
                  <li>Requiere permiso de cámara y sensores de movimiento.</li>
                  <li>Gira el teléfono para mirar alrededor.</li>
                  <li>Arrastra con un dedo para rotar el modelo.</li>
                  <li>Pellizca con dos dedos para escalar.</li>
                </ul>
                <a
                  href={androidArSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ar-open-scanner-btn"
                >
                  <ExternalLink size={16} />
                  Abrir Visor AR Android
                </a>
              </div>
            </div>
          )}

        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  role: state.auth.role,
  type: state.auth.type,
});

export default connect(mapStateToProps)(ARView);
